/**
 * Shared GitHub OAuth connect/disconnect helpers.
 */

import { getCurrentSession } from '@/lib/supabase';
import { githubIntegrationService } from '@/services/githubIntegrationService';

async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await getCurrentSession();
  if (!session) {
    throw new Error('Not authenticated. Please log in first.');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
  };
}

function isNetworkFetchError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = error.message.toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('network') || msg.includes('load failed');
}

/** Redirects to GitHub OAuth authorize URL. */
export async function startGitHubOAuth(): Promise<void> {
  let response: Response;
  try {
    const headers = await getAuthHeaders();
    response = await fetch('/api/github/oauth/authorize', { headers });
  } catch (error: unknown) {
    if (isNetworkFetchError(error)) {
      throw new Error('GitHub API server not running. Start the app with `npm run dev`.');
    }
    throw error;
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    const message = (err as { error?: string }).error || 'Failed to initiate GitHub OAuth';
    if (
      response.status === 500 &&
      (message.includes('not configured') || message.includes('GITHUB_CLIENT_ID'))
    ) {
      throw new Error(
        'GitHub OAuth is not configured on the server. Copy .env.example to .env.local, set GITHUB_CLIENT_ID and GITHUB_CLIENT_SECRET from a GitHub OAuth App, then restart with npm run dev.'
      );
    }
    throw new Error(message);
  }
  const { authUrl } = await response.json();
  if (!authUrl || typeof authUrl !== 'string') {
    throw new Error('Invalid OAuth response');
  }
  window.location.href = authUrl;
}

/** Removes stored GitHub integration for the current user (Supabase client + RLS). */
export async function disconnectGitHub(): Promise<void> {
  await githubIntegrationService.disconnect();
}
